/* eslint-disable react/no-danger */
import React, { useEffect, useState } from 'react';
import { Observer } from 'mobx-react';
import { Typography } from '@material-ui/core';
import { css } from '@emotion/core';
import ReactTable from 'react-table-6';
import styled from '@emotion/styled';
import { toJS } from 'mobx';
import { CustomSelect } from '../../util/CustomCreatableSelect';
import { useRootStore } from '../../../stores/BridgeProvider';
import Spinner from '../../util/Spinner';
import { safeEval } from '../../util/UtilFunctions';
import { MenuList, filterOptions } from '../../util/ReactSelectOverrides';

const Container = css`
  padding: 1rem 0rem;
  .rt-td {
    overflow: visible;
  }
  .ReactTable {
    min-width: 600px;
    max-width: 1200px;
    margin: 0 auto;
  }
  h4 {
    text-align: center;
  }
  .cell-style {
    height: 100%;
    display: flex;
    align-items: center;
  }
`;

const Headers = css`
  margin: 0 auto;
  display: flex;
  flex-flow: column nowrap;
  max-width: 1200px;
  .warning {
    color: red;
    align-self: center;
  }
  .save-state {
    align-self: flex-end;
    font-size: 16px;
  }
`;

const savingStateEnum = ['No changes', 'Changes saved', 'Saving changes'];

const Warning = () => {
  const [display, setDisplay] = useState('initial');
  return (
    <div className="warning" style={{ display }}>
      <h4>Warning:</h4>
      <p>
        Changes made here will be made permanent in your accounting data
        configuration.
        <br />
        We highly recommend creating a test account through the process outlined{' '}
        <a
          style={{ color: 'blue', textDecoration: 'none' }}
          href="https://support.dealmap.com/a/solutions/articles/67000432931"
          target="_blank"
          rel="noopener noreferrer"
        >
          here
        </a>{' '}
        before making any changes to your production data.
        <br />
        <span
          onClick={() => setDisplay('none')}
          style={{ color: 'blue', cursor: 'pointer' }}
        >
          [x] click to hide
        </span>
      </p>
    </div>
  );
};

const XdAccountMapping = () => (
  <Observer
    render={() => {
      const { authStore } = useRootStore();
      const [xdAccountMapping, setXdAccountMapping] = useState([]);
      const [dxAccountMapping, setDxAccountMapping] = useState([]);
      const [isLoading, setLoading] = useState(true);
      const [savingState, setSavingState] = useState(0);
      const fetchXdAccountMapping = async () => {
        try {
          const { data } = await authStore.request.get(
            `${process.env.REACT_APP_API}/api/xer-xd-account-mapping`,
          );
          setXdAccountMapping(data);
        } catch (e) {
          console.error(e);
        }
      };
      const fetchDxAccountMapping = async () => {
        try {
          const { data } = await authStore.request.get(
            `${process.env.REACT_APP_API}/api/xer-dx-account-mapping`,
          );
          setDxAccountMapping(data);
        } catch (e) {
          console.error(e);
        }
      };
      const initialActions = async () => {
        await Promise.all([fetchXdAccountMapping(), fetchDxAccountMapping()]);
        setLoading(false);
      };
      useEffect(() => {
        initialActions();
      }, []);

      const updateXdAccountMapping = async ({ XAccountId, DAccountId }) => {
        try {
          setSavingState(2);
          await authStore.request.post(
            `${process.env.REACT_APP_API}/api/xer-xd-account-mapping`,
            {
              xAccountId: XAccountId,
              dAccountId: DAccountId,
            },
          );
        } catch (e) {
          console.error(e);
          setLoading(true);
          initialActions();
        }
        setSavingState(1);
      };

      const deleteXdAccountMapping = async ({ XAccountId }) => {
        try {
          setSavingState(2);
          await authStore.request.delete(
            `${process.env.REACT_APP_API}/api/xer-xd-account-mapping`,
            {
              data: { xAccountId: XAccountId },
            },
          );
        } catch (e) {
          console.error(e);
          setLoading(true);
          initialActions();
        }
        setSavingState(1);
      };

      if (isLoading) return <Spinner />;
      const renderTestSelect = ({ index }) => {
        return (
          <div>
            <CustomSelect
              isSearchable
              isClearable
              // placeholder="Deal Account"
              components={{ MenuList }}
              options={dxAccountMapping}
              getOptionLabel={({ DAccountDesc }) => DAccountDesc || 'N/A'}
              getOptionValue={({ DAccountId }) => DAccountId}
              value={xdAccountMapping[index]}
              filterOption={filterOptions}
              onChange={val => {
                const data = [...xdAccountMapping];
                if (!val) {
                  data[index].DAccountId = null;
                  data[index].DAccountDesc = null;
                  deleteXdAccountMapping(data[index]);
                } else {
                  data[index].DAccountId = val.DAccountId;
                  data[index].DAccountDesc = val.DAccountDesc;
                  updateXdAccountMapping(data[index]);
                }
                setXdAccountMapping(data);
              }}
            />
          </div>
        );
      };
      return (
        <div css={Container}>
          <div css={Headers}>
            <Warning />
            <Typography className="save-state" variant="body1">
              {savingStateEnum[savingState]}
            </Typography>
          </div>
          <ReactTable
            data={xdAccountMapping}
            columns={[
              {
                Header: () => <h3>Accounting System Account</h3>,
                accessor: 'XAccountDesc',
                Cell: row =>
                  row.original.XAccountDesc && (
                    <div className="cell-style">
                      {' '}
                      {row.original.XAccountDesc}{' '}
                    </div>
                  ),
              },
              {
                Header: () => <h3>Deal Map Account</h3>,
                accessor: 'DAccountId',
                Cell: renderTestSelect,
              },
            ]}
          />
        </div>
      );
    }}
  />
);

export default XdAccountMapping;
